import { Injectable, inject } from '@angular/core';
import { createStore } from '@ngneat/elf';
import {
  withEntities,
  addEntities,
  selectManyByPredicate,
  deleteEntities,
  upsertEntitiesById,
  selectAllEntities,
  deleteAllEntities,
  upsertEntities,
} from '@ngneat/elf-entities';
import {
  Observable,
  EMPTY,
  expand,
  catchError,
  forkJoin,
  of,
  tap,
  filter,
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
  ErrorService,
  LoadingService,
} from '@digital-platform/shared/services';
import {
  UserSettings,
  UserSettingsObject,
} from '@digital-platform/users/domain';
import {
  LoginSessionFacadeService,
  UserSettingsService,
} from '@digital-platform/users/services';
import {
  ExpressWasteProfile,
  SpecialWasteProfile,
  WasteProfileAdminCard,
  WasteProfileCustomerCard,
  InternalStatus,
  WasteProfileStatus,
} from '@digital-platform/waste-profiles/domain';
import { WasteProfilesService } from '../waste-profiles/waste-profiles.service';

const internalStatusCardsStore = createStore(
  { name: 'wasteProfileCards' },
  withEntities<WasteProfileAdminCard>()
);

const statusCardsStore = createStore(
  { name: 'statusCards' },
  withEntities<WasteProfileCustomerCard>()
);

@Injectable({ providedIn: 'root' })
export class WasteProfileCardsService {
  // Internal Status Card Observables
  public coordinatorApproved$: Observable<WasteProfileAdminCard[]>;
  public analystApproved$: Observable<WasteProfileAdminCard[]>;
  public analystReviewed$: Observable<WasteProfileAdminCard[]>;

  // Status Card Observables
  public statusCards$: Observable<WasteProfileCustomerCard[]>;

  // flags to prevent multiple calls to load cards
  private loadedStatusCards = false;
  private loadingStatusCards = false;
  private loadedInternalStatusCards = false;
  private loadingInternalStatusCards = false;
  private destroy$ = false;

  // value updated from loginFacade subscription to accessList$
  private accessList: string[] = [];
  private wpLoadingUuid: string;

  // injected services
  private readonly wasteProfilesService = inject(WasteProfilesService);
  private readonly errorService = inject(ErrorService);
  private readonly loginFacade = inject(LoginSessionFacadeService);
  private readonly loadingService = inject(LoadingService);
  private readonly userSettingsService = inject(UserSettingsService);

  constructor() {
    this.coordinatorApproved$ = internalStatusCardsStore.pipe(
      selectManyByPredicate(
        (entity) =>
          entity.internalStatus === InternalStatus.COORDINATOR_APPROVED
      )
    );

    this.analystApproved$ = internalStatusCardsStore.pipe(
      selectManyByPredicate(
        (entity) => entity.internalStatus === InternalStatus.ANALYST_APPROVED
      )
    );

    this.analystReviewed$ = internalStatusCardsStore.pipe(
      selectManyByPredicate(
        (entity) => entity.internalStatus === InternalStatus.ANALYST_REVIEW
      )
    );

    this.statusCards$ = statusCardsStore.pipe(selectAllEntities());

    forkJoin([
      this.loginFacade.accessList$,
      this.userSettingsService.settings$.pipe(filter((settings) => !!settings)),
    ])
      .pipe(
        tap(([accessList]) => {
          if (accessList.length === 0) {
            this.wpLoadingUuid = '';
            this.clearCards();
          }
        })
      )
      .subscribe(([accessList, settings]) =>
        this.loadInternalCustomerCards(accessList, settings)
      );
  }

  public loadInternalCustomerCards(
    accessList: string[],
    settings: Partial<UserSettingsObject>
  ) {
    this.accessList = accessList;
    if (settings.divisions && settings.divisions.length > 0) {
      this.wpLoadingUuid = uuidv4();
      this.loadInternalStatusCards(settings.divisions).subscribe();
    } else if (accessList.length > 0 && accessList.includes('*')) {
      this.wpLoadingUuid = uuidv4();
      this.loadInternalStatusCards().subscribe();
    }
  }

  public loadExternalCustomerCards(
    accessList: string[],
    settings: Partial<UserSettingsObject>
  ) {
    this.accessList = accessList;
    if (settings.divisions && settings.divisions.length > 0) {
      this.wpLoadingUuid = uuidv4();
      this.loadStatusCards(settings.divisions).subscribe();
    } else if (accessList.length > 0 && accessList.includes('*')) {
      this.wpLoadingUuid = uuidv4();
      this.loadStatusCards().subscribe();
    }

    if (accessList.length > 0 && !accessList.includes('*')) {
      this.wpLoadingUuid = uuidv4();
      this.loadStatusCards().subscribe();
    }
  }

  public updateCard(profile) {
    const internalStatusCard = this.getAdminCardFromProfile(profile);
    const statusCard = this.getCustomerCardFromProfile(profile);

    internalStatusCardsStore.update(
      upsertEntitiesById(profile.id, {
        updater: (entity) => ({ ...entity, ...internalStatusCard }),
        creator: () => internalStatusCard,
      })
    );

    statusCardsStore.update(
      upsertEntitiesById(profile.id, {
        updater: (entity) => ({ ...entity, ...statusCard }),
        creator: () => statusCard,
      })
    );
  }

  public deleteCard(id: string) {
    internalStatusCardsStore.update(deleteEntities(id));
    statusCardsStore.update(deleteEntities(id));
  }

  getInternalStatusCardsCalls(divisionNumbers?: string[]) {
    return divisionNumbers && divisionNumbers.length > 0
      ? divisionNumbers.map((divisionNumber) =>
          this.loadInternalStatusCardsLoop(divisionNumber)
        )
      : [this.loadInternalStatusCardsLoop()];
  }

  public loadInternalStatusCards(divisionNumbers?: string[]) {
    if (!this.loadedInternalStatusCards && !this.loadingInternalStatusCards) {
      this.loadingInternalStatusCards = true;
      this.loadingService.startLoading();
      const calls = this.getInternalStatusCardsCalls(divisionNumbers);
      return forkJoin(calls).pipe(
        tap(() => {
          this.loadingInternalStatusCards = false;
          this.loadedInternalStatusCards = true;
          this.loadingService.stopLoading();
        })
      );
    }

    return of(null);
  }

  public loadStatusCards(divisionNumbers?: string[]) {
    if (!this.loadingStatusCards && !this.loadedStatusCards) {
      this.loadingStatusCards = true;
      this.loadingService.startLoading();
      const calls = [];
      if (divisionNumbers && divisionNumbers.length > 0) {
        divisionNumbers.forEach((divisionNumber) => {
          calls.push(this.statusCardsLoadingCalls(null, divisionNumber));
        });
      } else if (this.accessList.includes('*')) {
        calls.push(this.statusCardsLoadingCalls('*'));
      } else {
        this.accessList.forEach((customerName) => {
          //console.log('CUSTOMER ONLY:::', customerName);
          calls.push(this.statusCardsLoadingCalls(customerName));
        });
      }
      return forkJoin(calls).pipe(
        tap(() => {
          this.loadingStatusCards = false;
          this.loadedStatusCards = true;
          this.loadingService.stopLoading();
          this.wpLoadingUuid = '';
        })
      );
    }

    return of(null);
  }

  public clearCards() {
    internalStatusCardsStore.update(deleteAllEntities());
    statusCardsStore.update(deleteAllEntities());
    this.loadedStatusCards = false;
    this.loadingStatusCards = false;
    this.loadedInternalStatusCards = false;
    this.loadingInternalStatusCards = false;
  }

  public statusCardsLoadingCalls(
    customerName?: string,
    divisionNumber?: string
  ) {
    return forkJoin([
      this.loadStatusCardsLoop(
        WasteProfileStatus.EXPIRING,
        customerName,
        divisionNumber
      ),
      this.loadStatusCardsLoop(
        WasteProfileStatus.EXPIRED,
        customerName,
        divisionNumber
      ),
      this.loadStatusCardsLoop(
        WasteProfileStatus.APPROVED,
        customerName,
        divisionNumber
      ),
      this.loadStatusCardsLoop(
        WasteProfileStatus.ACTION_REQUIRED,
        customerName,
        divisionNumber
      ),
      this.loadStatusCardsLoop(
        WasteProfileStatus.NOT_SUBMITTED,
        customerName,
        divisionNumber
      ),
      this.loadStatusCardsLoop(
        WasteProfileStatus.PENDING_SIGNATURE,
        customerName,
        divisionNumber
      ),
      this.loadStatusCardsLoop(
        WasteProfileStatus.SUBMITTED,
        customerName,
        divisionNumber
      ),
      this.loadStatusCardsLoop(
        WasteProfileStatus.INCOMPLETE,
        customerName,
        divisionNumber
      ),
    ]);
  }

  private loadStatusCardsLoop(
    status: WasteProfileStatus,
    customerName?: string,
    divisionNumber?: string
  ) {
    return this.wasteProfilesService
      .findAllStatusCards({
        status,
        customerName,
        divisionNumber,
        wpLoadingUuid: this.wpLoadingUuid,
      })
      .pipe(
        catchError((error) => {
          this.errorService.raiseError(error);
          return EMPTY;
        }),
        expand((response) => {
          if (response?.Items && response.Items.length > 0) {
            statusCardsStore.update(addEntities(response.Items));
          }
          if (!response.LastEvaluatedKey) {
            // no LastEvaluatedKey means no additional pages
            return EMPTY;
          }

          return this.wasteProfilesService.findAllStatusCards({
            status,
            customerName,
            divisionNumber,
            ExclusiveStartKey: response?.LastEvaluatedKey,
            wpLoadingUuid: this.wpLoadingUuid,
          });
        })
      );
  }

  private loadInternalStatusCardsLoop(divisionNumber?: string) {
    return this.wasteProfilesService
      .findAllInternalStatusCards({
        divisionNumber,
        wpLoadingUuid: this.wpLoadingUuid,
      })
      .pipe(
        catchError((error) => {
          this.errorService.raiseError(error);
          return EMPTY;
        }),
        expand((response) => {
          if (response?.Items && response.Items.length > 0) {
            internalStatusCardsStore.update(upsertEntities(response.Items));
          }
          if (!response.LastEvaluatedKey || this.destroy$) {
            // no LastEvaluatedKey means no additional pages, so we can stop the loop and reset the loading uuid
            return EMPTY;
          }

          return this.wasteProfilesService.findAllInternalStatusCards({
            divisionNumber,
            ExclusiveStartKey: response?.LastEvaluatedKey,
            wpLoadingUuid: this.wpLoadingUuid,
          });
        })
      );
  }

  private getCustomerCardFromProfile(
    profile: ExpressWasteProfile | SpecialWasteProfile
  ): WasteProfileCustomerCard {
    return {
      schemaVersion: profile.schemaVersion,
      type: profile.type,
      lastModifiedDate: profile.lastModifiedDate,
      status: profile.status,
      internalStatus: profile.internalStatus,
      expiryDate: profile.expiryDate,
      generator: profile.generator,
      name: profile.name,
      destination: profile.destination,
      number: profile.number,
      id: profile.id,
      isRecertification: profile.isRecertification,
      isChange: profile.isChange,
      customerName: profile.customerName,
      pendingRecertification: profile.pendingRecertification,
      pendingChangeRequest: profile.pendingChangeRequest,
      analyticalRequiredOnNextRecert: profile.analyticalRequiredOnNextRecert,
      signNowDocument: profile.signNowDocument,
      docusignData: profile.docusignData,
      stateApprovalWasteCode: profile.stateApprovalWasteCode,
      approvedDate: profile.approvedDate,
    };
  }

  private getAdminCardFromProfile(
    profile: ExpressWasteProfile | SpecialWasteProfile
  ): WasteProfileAdminCard {
    return {
      schemaVersion: profile.schemaVersion,
      type: profile.type,
      lastModifiedDate: profile.lastModifiedDate,
      status: profile.status,
      internalStatus: profile.internalStatus,
      expiryDate: profile.expiryDate,
      generator: profile.generator,
      name: profile.name,
      destination: profile.destination,
      number: profile.number,
      coordinator: profile.coordinator,
      approverName: profile.approverName,
      id: profile.id,
      approvedDate: profile.approvedDate,
      isRecertification: profile.isRecertification,
      isChange: profile.isChange,
      submittedDate: profile.submittedDate,
      customerName: profile.customerName,
    };
  }

  destroyInternalStatusCardsRequest(destroy: boolean) {
    this.destroy$ = destroy;
  }
}
